// src/web/client/src/pages/Home.jsx
//
// Home — the first page (UX brief, Phase B): the brain's overview for a store on
// the Platform plan (HomeBrain: diagram, what happened this week, what is
// coming up), then the revenue connected to the emails, the live email
// preview and the system status. Was Dashboard.jsx; the Quick-actions row and
// the Settings button are gone (everything they linked to is in the menu).
import React, { useState, useEffect, useReducer, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { Settings, AlertTriangle } from "lucide-react";

// Dashboard Components (from components/dashboard/)
import AttributionHero from "@/components/dashboard/AttributionHero";
import AIEmailShowcase from "@/components/dashboard/AIEmailShowcase";
import SystemStatus from "@/components/dashboard/SystemStatus";
// SimulateOrderDialog was removed from the dashboard: simulated orders are
// excluded from every merchant-facing number (data integrity), which makes
// the button meaningless for a real merchant. It will return per-shop when
// dev-store detection lands.

// Subscription Components
import SubscriptionAlert from "@/components/subscription/SubscriptionAlert";

// The brain's overview — Platform plan only, lazily loaded with Polaris.
import { PlatformGate, PLATFORM_PAGES } from "@/pages/Platform";
import { useSubscriptionStatus } from "@/components/subscription/SubscriptionGuard";
import PageHeader from "@/components/PageHeader";

// API imports
import { API_BASE_URL } from "@/constants";
import { authenticatedFetch } from "@/utils/authenticatedFetch";
import { normalizeShopDisplayName } from "@/utils/shopName";
// B5 invariant — a failed load never destroys data already on screen.
// Pure reducer, unit-tested in utils/__tests__/dashboardLoad.test.js.
import { dashboardLoadReducer, initialLoadState } from "@/utils/dashboardLoad";
// THE single shop resolver (priority-3): URL ?shop= wins over localStorage,
// stale shop-scoped keys purged on switch. Kills the sticky-shop bug.
import { getShopDomain as resolveShopDomain } from "@/utils/shopContext";

// ============================================
// CHART DATA UTILITIES
// ============================================

/**
 * Format date string to short format (e.g., "15.6")
 */
const formatDate = (dateStr) => {
  if (!dateStr || !dateStr.includes('-')) return dateStr;
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  return `${day}.${month}`;
};

/**
 * Smart limit chart data to reasonable number of points
 */
const smartLimitChartData = (chartData, maxPoints = 14) => {
    if (!chartData || !chartData.labels || !chartData.data) return chartData;

  const totalPoints = chartData.labels.length;

  // If few points, just format dates
  if (totalPoints <= maxPoints) {
    return {
      labels: chartData.labels.map(formatDate),
      data: chartData.data
    };
  }

  // Find where we have data (non-zero)
  const dataPoints = [];
  chartData.data.forEach((value, index) => {
    if (value > 0) {
      dataPoints.push(index);
    }
  });

  // If no data, take last N days
  if (dataPoints.length === 0) {
    const start = totalPoints - maxPoints;
    return {
      labels: chartData.labels.slice(start).map(formatDate),
      data: chartData.data.slice(start)
    };
  }

  // Take range around the data
  const firstData = Math.min(...dataPoints);
  const lastData = Math.max(...dataPoints);

  const start = Math.max(0, firstData - 2);
  const end = Math.min(totalPoints - 1, lastData + 2);

  const slicedLabels = chartData.labels.slice(start, end + 1);
  const slicedData = chartData.data.slice(start, end + 1);

  // Sample if still too many points
  if (slicedLabels.length > maxPoints) {
    const step = Math.ceil(slicedLabels.length / maxPoints);
    const sampledLabels = [];
    const sampledData = [];

    for (let i = 0; i < slicedLabels.length; i += step) {
      sampledLabels.push(formatDate(slicedLabels[i]));
      sampledData.push(slicedData[i]);
    }

    return { labels: sampledLabels, data: sampledData };
  }

  return {
    labels: slicedLabels.map(formatDate),
    data: slicedData
  };
};
/**
 * Convert message_breakdown to { labels, data } for chart
 */
const buildMessagesChartFromBreakdown = (breakdown) => {
  if (!breakdown || typeof breakdown !== "object") {
    return { labels: [], data: [] };
  }

  // סדר קבוע של סוגי ההודעות המוכרות
  const order = [
    "thank_you",
    "review_request",
    "vip_upsell",
    "custom_offer",
  ];

  const labelMap = {
    thank_you: "Thank You",
    review_request: "Review Request",
    vip_upsell: "VIP Upsell",
    custom_offer: "Custom Offer",
  };

  const labels = [];
  const values = [];

  // קודם כל הסוגים המוכרים
  order.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(breakdown, key)) {
      labels.push(labelMap[key] || key);
      values.push(Number(breakdown[key] || 0));
    }
  });

  // אחר כך כל מפתח אחר שלא טופל (אם יהיה בעתיד)
  Object.keys(breakdown).forEach((key) => {
    if (!order.includes(key)) {
      labels.push(key);
      values.push(Number(breakdown[key] || 0));
    }
  });

  return { labels, data: values };
};

// Check if chart has any meaningful (non-zero) data
// ✅ נחשב כ"יש נתונים" אם יש מערך data לא ריק
// האם יש באמת נתונים להצגה בגרף (לפחות ערך אחד גדול מ-0)
const hasMeaningfulData = (chartData) => {
  if (!chartData || !Array.isArray(chartData.data)) {
    return false;
  }

  // רק אם יש לפחות נקודה אחת > 0 נציג גרף
  return chartData.data.some((v) => Number(v) > 0);
};


// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================

export default function Home() {
  const navigate = useNavigate();
  
  // State
  const [shop, setShop] = useState(null);
  // data + error travel together through the reducer so a failure can never
  // clobber data (the old catch fabricated zeroed stats — a 1-second network
  // blip during auto-refresh wiped a live revenue number to $0).
  const [{ data: dashboardData, error: loadError }, dispatchLoad] = useReducer(
    dashboardLoadReducer,
    initialLoadState
  );
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Subtle in-place indicator for range-change refreshes (B6) — the 60s
  // background auto-refresh deliberately shows nothing.
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Reported by AIEmailShowcase (it already fetches /templates): whether the
  // shop has REAL templates. Drives the honest "set up your first template"
  // line in the welcome strip — null until known.
  const [hasRealTemplates, setHasRealTemplates] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState("week");
  // First load shows the full loading screen; every later fetch (range
  // change, auto-refresh, retry) updates in place.
  const isFirstLoadRef = useRef(true);

  // Shop identity comes from the shared resolver ONLY (utils/shopContext):
  // URL ?shop= is authoritative, storage is the SPA fallback, null renders
  // the honest error state below. The old local copy read storage FIRST,
  // which is exactly the sticky-shop bug the server logs caught red-handed
  // (`shop=ecomoria-test` sent while inside billing-dev2).
  const shopDomain = resolveShopDomain();
  // The brain's section renders only when the plan includes it; otherwise Home
  // is exactly the page every other plan had.
  const subscription = useSubscriptionStatus();
  const hasBrain = subscription?.hasFeatureAccess?.('platform_access') === true;

  /**
   * Build API URL based on time range
   */
  const buildApiUrl = (timeRange) => {
    const periods = {
      week: "7d",
      month: "30d",
      quarter: "90d"
    };
    
    if (periods[timeRange]) {
      return `${API_BASE_URL}/dashboard?shop_domain=${shopDomain}&period=${periods[timeRange]}`;
    }
    
    // Default to 6 months
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 6);
    return `${API_BASE_URL}/dashboard?shop_domain=${shopDomain}&date_from=${startDate.toISOString().split("T")[0]}&date_to=${endDate.toISOString().split("T")[0]}`;
  };

  const loadData = async ({ silent = false } = {}) => {
    console.log("🚀 Loading dashboard data...");
    if (!silent) {
      setIsLoading(true);
    }
  
    try {
      const apiUrl = buildApiUrl(selectedTimeRange);
      console.log("🔗 Fetching from:", apiUrl);
  
      //const response = await fetch(apiUrl);
      const response = await authenticatedFetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error("API returned non-JSON response");
      }
  
      const data = await response.json();
      console.log("📊 Dashboard data received:", data);
  
      const normalizedData = normalizeApiResponse(data);
      dispatchLoad({ type: "loaded", data: normalizedData });

// 🕵️ לוג בדיקה – מה באמת מגיע לדשבורד
console.log("📊 DASHBOARD NORMALIZED:", normalizedData);
      if (data?.shop) {
        setShop(data.shop);
      }

      await loadSubscriptionInfo();
    } catch (error) {
      console.error("❌ Error loading dashboard:", error);
      // HONEST FAILURE (B5): never fabricate zeroed stats here. The reducer
      // keeps whatever data was already on screen and records the error, so
      // the UI can show "couldn't refresh" instead of a fake empty store.
      dispatchLoad({ type: "failed", error: error.message });
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };
  
  /**
   * Normalize API response to UI structure
   */
  const normalizeApiResponse = (data) => {
    // Convert arrays to {labels, data} format
    const toLabelsData = (src) => {
      if (!src) return { labels: [], data: [] };
      if (Array.isArray(src)) {
        if (src.length && typeof src[0] === "object") {
          return {
            labels: src.map((x) => x.label ?? String(x.name ?? "")),
            data: src.map((x) => Number(x.value ?? x.count ?? 0)),
          };
        }
        return { labels: [], data: [] };
      }
      if (src.labels && src.data) return src;
      return { labels: [], data: [] };
    };

    // Normalize charts
    if (data?.charts) {
      data.charts.revenue = toLabelsData(data.charts.revenue);
      data.charts.orders = toLabelsData(data.charts.orders);
    
      // ✅ אם יש message_breakdown – תמיד נבנה ממנו את הגרף
      if (data.message_breakdown) {
        data.charts.messages = buildMessagesChartFromBreakdown(data.message_breakdown);
      } else {
        // אחרת – נשתמש בפורמט הישן
        data.charts.messages = toLabelsData(
          data.charts.messages ?? data.charts.messageTypes
        );
      }
    }
    
    // Handle different response formats
    if (data?.data) {
      return data.data;
    } else if (data?.stats) {
      return data;
    } else if (data?.success && data?.metrics) {
      return {
        stats: {
          total_orders: data.metrics.total_orders || 0,
          total_revenue: data.metrics.total_revenue || 0,
          messages_sent: data.metrics.messages_sent || 0,
          messages_clicked: data.metrics.messages_clicked || 0,
          click_rate: data.metrics.click_rate || 0,
          scheduled_messages: data.metrics.scheduled_messages || 0,
          open_rate: data.metrics.open_rate || 0,
        },
        revenue_chart: data.charts?.revenue || { labels: [], data: [] },
       // messages_chart: data.charts?.messages || data.charts?.messageTypes || { labels: [], data: [] },
       messages_chart:
       data.message_breakdown
         ? buildMessagesChartFromBreakdown(data.message_breakdown)
         : (data.charts?.messages || data.charts?.messageTypes || { labels: [], data: [] }),     
       orders_overview: data.charts?.orders || { labels: [], data: [] },
        recent_orders: data.recent_orders || data.recentOrders || [],
        recent_messages: data.recent_messages || data.recentMessages || [],
      };
    }
   
    return data;
  };

  /**
   * Load subscription info
   */
  const loadSubscriptionInfo = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/subscription/info?shop_domain=${shopDomain}`);
      //const response = await fetch(`${API_BASE_URL}/subscription/info?shop_domain=${shopDomain}`);
      if (response.ok) {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const data = await response.json();
          setSubscriptionInfo(data);
        }
      }
    } catch (error) {
      console.error("Failed to load subscription info:", error);
    }
  };

  // Initial load + auto-refresh
  /*useEffect(() => {
    let isMounted = true;

    const run = async () => {
      if (!isMounted) return;
      await loadData();
    };

    run();
const intervalId = setInterval(() => {
      console.log("🔄 Auto-refreshing dashboard...");
      run();
    }, 60000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [selectedTimeRange]);*/
  useEffect(() => {
    // No identified shop → nothing to load; the error state renders instead.
    if (!shopDomain) return;

    let isMounted = true;

    const run = async (options) => {
      if (!isMounted) return;
      await loadData(options);
    };

    if (isFirstLoadRef.current) {
      // First load only — full loading screen.
      isFirstLoadRef.current = false;
      run({ silent: false });
    } else {
      // Range change (B6): refresh IN PLACE — no full-screen spinner that
      // blanks three live areas. A subtle inline indicator runs instead.
      setIsRefreshing(true);
      run({ silent: true }).finally(() => {
        if (isMounted) setIsRefreshing(false);
      });
    }

    // Auto-refresh כל 60 שניות – בלי מסך Loading
    const intervalId = setInterval(() => {
      console.log("🔄 Auto-refreshing dashboard...");
      run({ silent: true });
    }, 60000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [selectedTimeRange]);
  
  // Honest failure state: we don't know which store this is. Never fall
  // back to demo data here — that would show a merchant numbers that are
  // not theirs.
  if (!shopDomain) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center text-center py-10 space-y-4">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">We couldn't identify your store</h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              Please open Ecomoria from your Shopify admin (Apps → Ecomoria) so we
              can load your store's data.
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Reload
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Honest error state (B5) — load failed and there is NO previous data to
  // show. Distinct from the real empty state: a transient API failure must
  // never render as "your store is empty".
  if (loadError && !dashboardData) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center text-center py-10 space-y-4">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">We couldn't load your dashboard</h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              Something went wrong while fetching your data. Your store is fine —
              this is a loading problem on our side.
            </p>
            <Button onClick={() => loadData({ silent: false })} variant="outline">
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // "Brand-new merchant" detector — kept ONLY to decide whether the welcome
  // strip shows. It no longer gates the three areas: each of them has its own
  // honest empty state (hero → "$0, will appear here"; showcase → sample
  // order + sample template; system status → honest zeros), all built for
  // exactly this merchant and previously unreachable behind this flag.
  const hasAnyData = Boolean(
    dashboardData?.revenue_chart?.labels?.length ||
    dashboardData?.orders_overview?.labels?.length ||
    dashboardData?.messages_chart?.labels?.length ||
    dashboardData?.recent_orders?.length ||
    dashboardData?.recent_messages?.length
  );

  // Human label for the selected range — matches the dropdown wording so the
  // quick-action subtitles say what the numbers actually cover.
  const rangeLabel =
    { week: "the last 7 days", month: "the last 30 days", quarter: "the last 90 days" }[
      selectedTimeRange
    ] || "this period";

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header — the title is the menu item's name; the subtitle is the
            store's name as the server has it (one source: Shop.shop_name). */}
        <PageHeader
          title="Home"
          subtitle={normalizeShopDisplayName(shop?.shop_name) || "Welcome to Ecomoria"}
          actions={(
            <div className="flex gap-3 items-center">
              {isRefreshing && (
                <span
                  className="inline-block w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"
                  aria-label="Refreshing"
                />
              )}
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              >
                <option value="week">Last 7 days</option>
                <option value="month">Last 30 days</option>
                <option value="quarter">Last 90 days</option>
              </select>
            </div>
          )}
        />

        {/* Honest refresh-failure notice (B5): the load failed but we still
            have the last good data on screen — say so instead of wiping it. */}
        {loadError && dashboardData && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
            <p className="text-sm text-amber-800">
              Couldn't refresh — showing last loaded data.
            </p>
            <button
              onClick={() => loadData({ silent: true })}
              className="text-sm font-medium text-amber-800 underline hover:text-amber-900 shrink-0"
            >
              Retry
            </button>
          </div>
        )}

        {/* Subscription Alert */}
        {subscriptionInfo && (
          <SubscriptionAlert subscription={subscriptionInfo} />
        )}

        {/* Welcome strip — brand-new merchant only (no data yet). The three
            areas below render for EVERYONE; this is the only element that is
            conditioned on hasAnyData. Copy promises nothing the system isn't
            doing: the preview works right now (sample template fallback), and
            automation honestly requires a first template. */}
        {!hasAnyData && (
          <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-5 py-4">
            <p className="text-sm font-medium text-indigo-900">
              Ecomoria is connected to your store. Here's what your customer emails
              will look like — try a preview below.
            </p>
            {hasRealTemplates === false && (
              <p className="mt-1 text-xs text-indigo-700/80">
                Set up your first email template to turn on automation.
              </p>
            )}
            <Link to={"/automations"} className="inline-block mt-2">
              <Button variant="outline" size="sm">
                <Settings className="w-3.5 h-3.5 mr-1.5" />
                Configure Store
              </Button>
            </Link>
          </div>
        )}

        {/* No-templates notice — additive, and the counterpart to the welcome
            strip's nested nudge. The welcome strip only shows for a brand-new
            merchant (!hasAnyData); a merchant who already has orders but still
            has zero templates would otherwise see "Your system is working"
            while, in truth, NO automation email can fire. This honest banner
            covers exactly that previously-uncovered case. hasRealTemplates is
            null until AIEmailShowcase reports it, and stays null on a load
            failure, so we never claim "no templates" unless we actually
            checked and found none. */}
        {hasAnyData && hasRealTemplates === false && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
            <div>
              <p className="text-sm font-medium text-amber-900">
                No email templates yet — automation is off
              </p>
              <p className="mt-0.5 text-xs text-amber-800/80 leading-relaxed">
                Ecomoria can't send emails to your customers until you add a
                template. Set one up to turn on automation.
              </p>
            </div>
            <Link to={"/automations"} className="shrink-0">
              <Button variant="outline" size="sm">
                <Settings className="w-3.5 h-3.5 mr-1.5" />
                Set up templates
              </Button>
            </Link>
          </div>
        )}

        {/* Main Content — the three areas render unconditionally: each has its
            own honest empty state, so a new merchant sees the product working
            (hero at $0, live AI preview on a sample, honest zeros) instead of
            a bare welcome card. With data, this block is byte-identical to
            the previous gated version. */}
        <>
          {/* The brain's overview (Platform plan): diagram + what happened this
              week + what is coming up. PlatformGate renders its honest state
              (not connected / unavailable) instead when the Platform cannot be
              read; no header or frame of its own — this page's header is above. */}
          {hasBrain && (
            <PlatformGate>
              <PLATFORM_PAGES.homeBrain />
            </PlatformGate>
          )}

          {/* Revenue connected to the emails */}
          <AttributionHero
            period={{ week: "7d", month: "30d", quarter: "90d" }[selectedTimeRange] || "30d"}
          />

          {/* Area 2 — Your customer emails: interactive preview (real order + type, quota-free) */}
          <AIEmailShowcase
            shopDomain={shopDomain}
            onTemplatesLoaded={(count) => setHasRealTemplates(count > 0)}
          />

          {/* Area 3 — The system is working: activity, honest engagement, revenue by type */}
          {dashboardData?.stats && (
            <SystemStatus
              period={{ week: "7d", month: "30d", quarter: "90d" }[selectedTimeRange] || "30d"}
              stats={dashboardData.stats}
            />
          )}

        </>
      </div>
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

/**
 * Time Range Selector
 */
function TimeRangeSelector({ selected, options, onChange }) {
  const labels = {
    week: "Week",
    month: "Month",
    quarter: "Quarter"
  };

  return (
    <div className="flex gap-2">
      {options.map((option) => (
        <button
          key={option}
          className={`text-sm px-3 py-1 rounded-md font-medium ${
            selected === option
              ? "bg-gray-100"
              : "text-gray-500 hover:bg-gray-100"
          }`}
          onClick={() => onChange(option)}
        >
          {labels[option]}
        </button>
      ))}
    </div>
  );
}

// EmptyDashboard was removed in the same PR that replaced it: the welcome
// strip + the three areas' own honest empty states now serve the brand-new
// merchant (who previously saw ONLY the empty card, never the AI showcase
// that was built for them).